import React, { useState } from 'react';
import {
  checkForTokenInactivityTimeout,
  getCognitoToken,
  nullifyTokenRefreshTime,
} from 'initializers/awsAmplify/helpers';
import { storage } from 'lib/storage';
import { STUB_TOKEN } from 'domain/constants';
import Cookies from 'js-cookie';

import { updateAuthRequestData, updatePaymentAuthRequestData } from './helpers';
import { signOutAction } from 'domain/env';
import { CANARY_COOKIE_NAME } from 'lib/apiHelpers';

export class TokenKeeper {
  _store;

  _currentParams = {};

  injectStore = (store) => {
    this._store = store;
  };

  injectCurrentParams = (params) => {
    this._currentParams = params;
  };

  getClientToken = async () => {
    try {
      // throws exception in case inactivity timeout reached causing logout
      checkForTokenInactivityTimeout();

      // returns active or refreshes token so that we are always
      // dealing with fresh token
      const token = await getCognitoToken();
      return token;
    } catch (e) {
      nullifyTokenRefreshTime();
      // No current user - in this case signOut does nothing, must trigger signOutSuccess
      if (e === 'No current user') {
        // this is not proper solution
        // idea is to remove role so that after user is redirected to login and
        // logs in, app doesnt consider user authorized due to role being present
        // in LS
        storage().role().remove();
        storage().token().remove();
        this._store.dispatch({ type: signOutAction.success });
      } else {
        // there are 2 reasons for exception to be thrown:
        //
        // 1. getCognitoToken function didnt manage to refresh token due to
        //    number of reasons. In most cases it indicates refresh token
        //    expiration and user has no way to continue current auth session
        // 2. checkForTokenInactivityTimeout throws exception in case token
        //    wasn't refreshed for certain period that indicates user wasnt
        //    interacting with app and we force logout by throwing this particular
        //    exception

        // this is required as getClientToken might be consequently invoked
        // and set new refresh token timestamp after we nulified it inside
        // checkForTokenInactivityTimeout()

        this._store.dispatch({ type: signOutAction.type });
      }
    }
  };

  isDokkaLogin = () => !!this.getDokkaToken();

  // this is deprecated and is never expected to be invoked. Remove after first successful Cognito release
  getDokkaToken = () => {
    const storeToken = this._store.getState().env.get('dokkaToken') || storage().token().get();
    return storeToken && storeToken !== STUB_TOKEN ? storeToken : '';
  };

  getToken = async () => {
    const clientToken = await this.getClientToken();

    return clientToken;
  };

  getLanguage = () => this._store.getState().env.get('locale');

  getCompany = () => this._currentParams.companyId || null;

  getUsername = () => this._store.getState().env.get('userId') || '';

  getAccountId = () => this._store.getState().env.get('payModuleCompanyPermissions').first()?.get('accountId') || '';

  getRequestData = async () => {
    const token = await this.getToken();
    const lang = this.getLanguage();
    const company = this.getCompany();
    const deliveryMethod = Cookies.get(CANARY_COOKIE_NAME);
    const username = this.getUsername();
    const accountId = this.getAccountId();
    return {
      token,
      lang,
      company,
      [CANARY_COOKIE_NAME]: deliveryMethod,
      username,
      accountId,
    };
  };

  updateRequestData = async (requestData) => {
    const requestUpdateData = await this.getRequestData();
    return requestUpdateData ? updateAuthRequestData(requestData, requestUpdateData) : requestData;
  };

  updatePaymentRequestData = async (requestData) => {
    const requestUpdateData = await this.getRequestData();
    return requestUpdateData ? updatePaymentAuthRequestData(requestData, requestUpdateData) : requestData;
  };
}

const keeper = new TokenKeeper();
export const useApiToken = () => {
  const [token, setToken] = useState(null);
  keeper.getToken().then((res) => {
    if (token !== res) {
      setToken(res);
    }
  });
  return token || null;
};

export const withApiToken = (Component) => (props) => {
  const apiToken = useApiToken();

  return <Component {...props} apiToken={apiToken} />;
};

export default keeper;
